import {ExpertSystemEquipmentType, HistoricalDialogData} from '@domain/expert-system'
import {ExpertSystemHierarchyNodeType, KpiCalculationError} from '@hconnect/common/types'
import {CollapsableRow, PinnableHeaderTableCell, useIsMobile} from '@hconnect/uikit'
import {Edit} from '@mui/icons-material'
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material'
import {isNil} from 'lodash'
import React, {FC, useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  ESUR_TABLE_LEFTS,
  ESUR_TABLE_WIDTHS,
  EXPERT_SYSTEM_USAGE_TABLE_KEYS,
  FEATURE_NAMESPACE,
  MONTH_KEYS,
  STICKY_COLUMNS_CONFIG
} from '../consts'
import {
  useExpandCollapse,
  useRenderAverageCell,
  useRenderWeightedAverageCell,
  useTableCellStyle,
  useTableScrollState
} from '../hooks'
import {ExpertSystemUsageRateColumnKeys, HierarchyTableNode, MonthData} from '../types'
import {tableCellDataTestIdGenerator, getMonthsDetails, isKilnOrMillLevel} from '../utils'

import {CommentKpiDialog} from './CommentKpiDialog'
import {LocationCellContent} from './LocationCellContent'
import {MonthCell} from './MonthCell'

type ExpertSystemTableCellEditConfig = {
  isEditMode: boolean
  onEditClick: (item: HierarchyTableNode) => void
  onMonthValueChange: (itemId: string, month: string, value?: number | KpiCalculationError) => void
  setNodeMonth: (data: HistoricalDialogData) => void
  year: number
  equipmentType: ExpertSystemEquipmentType
  setCommentPopup: (popupData: {item?: HierarchyTableNode; month: string}) => void
}

const useRenderExpertSystemTableCell = ({
  isEditMode,
  onMonthValueChange,
  setNodeMonth,
  onEditClick,
  setCommentPopup,
  year
}: ExpertSystemTableCellEditConfig) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const handleEditClick = useCallback(
    (item: HierarchyTableNode) => onEditClick(item),
    [onEditClick]
  )

  const renderAverageCell = useRenderAverageCell({
    renderActions: (item) => (
      <IconButton
        data-test-id={`expert-system-usage-edit-person-button-${item.id}`}
        sx={(theme) => ({
          visibility:
            isEditMode && item.type === ExpertSystemHierarchyNodeType.Plant ? 'visible' : 'hidden',
          color: theme.palette.primary.light
        })}
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          handleEditClick(item)
        }}
      >
        <Edit />
      </IconButton>
    )
  })

  const renderWeightedAverageCell = useRenderWeightedAverageCell()

  const renderMillTypeCell = (item: HierarchyTableNode) => (
    <>{item.millType && t(`label.${item.millType}`)}</>
  )

  const renderMonthCell = (item: HierarchyTableNode, key: string, monthData: MonthData) => (
    <MonthCell
      hierarchyId={item.id}
      month={key}
      year={year}
      monthData={monthData}
      itemType={item.type}
      isEditMode={isEditMode && isKilnOrMillLevel(item.type)}
      onMonthValueChange={onMonthValueChange}
      canEdit={item.access.canWriteNode}
      setNodeMonth={(id) => setNodeMonth({hierarchyId: id, month: key, equipmentName: item.name})}
      onCommentClick={
        isKilnOrMillLevel(item.type) &&
        !isEditMode &&
        (!isNil(monthData?.value) || !isNil(monthData?.kpiCalculationError))
          ? () => setCommentPopup({item, month: key})
          : undefined
      }
    />
  )

  return (item: HierarchyTableNode, columnIndex: number, columnKey: string): React.ReactNode => {
    const dataTestId = tableCellDataTestIdGenerator(item.id, columnKey)
    const key = columnKey as ExpertSystemUsageRateColumnKeys

    if (key === ExpertSystemUsageRateColumnKeys.LOCATION) {
      return (
        <LocationCellContent
          name={item.name}
          countryCode={item.countryCode}
          dataTestId={dataTestId}
        />
      )
    }
    if (key === ExpertSystemUsageRateColumnKeys.MILL_TYPE) {
      return renderMillTypeCell(item)
    }
    if (key === ExpertSystemUsageRateColumnKeys.AVERAGE) {
      return renderAverageCell(item)
    }
    if (key === ExpertSystemUsageRateColumnKeys.WEIGHTED_AVERAGE) {
      return renderWeightedAverageCell(item)
    }
    if (MONTH_KEYS.includes(key)) {
      const monthData = item[key.toLowerCase() as keyof HierarchyTableNode] as MonthData
      return renderMonthCell(item, key, monthData)
    }
    return typeof item[key] === 'number' || typeof item[key] === 'string' ? item[key] : null
  }
}

type ExpertSystemUsageTableProps = {
  data: HierarchyTableNode[]
  getIsExpanded: (id: string) => boolean
  toggleExpandCollapse: (id: string) => void
  showMillColumns: boolean
  year: number
  equipmentType: ExpertSystemEquipmentType
  isEditMode: boolean
  onEditClick: (item: HierarchyTableNode) => void
  onMonthValueChange: (itemId: string, month: string, value?: number | KpiCalculationError) => void
  setNodeMonth: (data: HistoricalDialogData) => void
  setCommentPopup?: (popupData: {item?: HierarchyTableNode; month?: string}) => void
}

export const ExpertSystemUsageTable: FC<ExpertSystemUsageTableProps> = ({
  data,
  toggleExpandCollapse,
  getIsExpanded,
  showMillColumns,
  year,
  equipmentType,
  isEditMode,
  onEditClick,
  onMonthValueChange,
  setNodeMonth
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)

  const [commentPopup, setCommentPopup] = useState<{
    item?: HierarchyTableNode
    month: string
  } | null>(null)

  const {scrollingRight, scrollingLeft, handleScroll} = useTableScrollState()
  const {isItemExpanded, onItemToggled} = useExpandCollapse(getIsExpanded, toggleExpandCollapse)

  const getCellStyle = useTableCellStyle({
    stickyLeft: STICKY_COLUMNS_CONFIG.left,
    stickyRight: STICKY_COLUMNS_CONFIG.right,
    defaultWidth: ESUR_TABLE_WIDTHS.OTHER,
    scrollingRight,
    scrollingLeft,
    backgroundColor: theme.palette.background.paper,
    hoverBackgroundColor: theme.palette.action.hover
  })

  const renderExpertSystemTableCell = useRenderExpertSystemTableCell({
    isEditMode,
    year,
    equipmentType,
    onEditClick,
    onMonthValueChange,
    setNodeMonth,
    setCommentPopup
  })

  const language = i18n.language
  const monthDetails = getMonthsDetails(language)

  const monthData =
    commentPopup?.item && commentPopup?.month
      ? (commentPopup.item[
          commentPopup.month.toLowerCase() as keyof HierarchyTableNode
        ] as MonthData)
      : undefined

  return (
    <TableContainer
      onScroll={handleScroll}
      sx={{overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)'}}
    >
      <Table stickyHeader sx={{tableLayout: 'fixed'}}>
        <TableHead>
          <TableRow>
            <PinnableHeaderTableCell
              left={ESUR_TABLE_LEFTS.LOCATION}
              width={ESUR_TABLE_WIDTHS.LOCATION}
              showShadow={isMobile && scrollingRight}
              isSticky
            >
              {t('label.location')}
            </PinnableHeaderTableCell>
            {showMillColumns && (
              <>
                <PinnableHeaderTableCell
                  left={ESUR_TABLE_LEFTS.MILL_TYPE}
                  width={ESUR_TABLE_WIDTHS.MILL_TYPE}
                  showShadow={isMobile && scrollingRight}
                  isSticky
                >
                  {t('label.type')}
                </PinnableHeaderTableCell>
                <PinnableHeaderTableCell
                  left={ESUR_TABLE_LEFTS.EXPERT_SYSTEM_NAME}
                  width={ESUR_TABLE_WIDTHS.EXPERT_SYSTEM_NAME}
                  showShadow={isMobile && scrollingRight}
                  isSticky
                >
                  {t('label.expertSystemName')}
                </PinnableHeaderTableCell>
              </>
            )}
            {monthDetails.names.map((monthName) => (
              <TableCell
                sx={{width: `${ESUR_TABLE_WIDTHS.MONTH}px`}}
                key={`tableHeader-${monthName}`}
              >
                {monthName}
              </TableCell>
            ))}
            <PinnableHeaderTableCell
              right={ESUR_TABLE_WIDTHS.WEIGHTED_AVERAGE}
              width={ESUR_TABLE_WIDTHS.AVERAGE}
              isSticky
              showShadow={isMobile && scrollingLeft}
            >
              {t('label.average')}
            </PinnableHeaderTableCell>
            <PinnableHeaderTableCell
              right={0}
              width={ESUR_TABLE_WIDTHS.WEIGHTED_AVERAGE}
              isSticky
              showShadow={false}
            >
              {t('label.weightedAverage')}
            </PinnableHeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <CollapsableRow
              key={item.id}
              item={item}
              isItemExpanded={isItemExpanded}
              onExpandToggleClicked={onItemToggled}
              columnKeys={
                showMillColumns
                  ? EXPERT_SYSTEM_USAGE_TABLE_KEYS
                  : [
                      ...EXPERT_SYSTEM_USAGE_TABLE_KEYS.slice(0, 1),
                      ...EXPERT_SYSTEM_USAGE_TABLE_KEYS.slice(3)
                    ]
              }
              renderCell={renderExpertSystemTableCell}
              getCellStyle={getCellStyle}
              expandCollapseOnlyOnFirstColumn
            />
          ))}
        </TableBody>
      </Table>
      {commentPopup && (
        <CommentKpiDialog
          {...commentPopup}
          progress={monthData?.value}
          kpiCalculationError={monthData?.kpiCalculationError}
          kpiTargetEquipmentType={equipmentType}
          hierarchyId={commentPopup.item?.id ?? ''}
          year={year}
          onClose={() => setCommentPopup(null)}
        />
      )}
    </TableContainer>
  )
}
